import { LazyCategoryWell } from "owa-categories";
import useLogMissingCategory from "../utils/useLogMissingCategory";
import type { MailListItemSingleLineViewProps } from "./MailListItemComponentProps";
import onCategoryClicked from "../utils/onCategoryClicked";
import renderReactions from "../utils/renderReactions";
import formatFlagDueDate from "../utils/formatFlagDueDate";
import isValidEmailAddress from "../utils/isValidEmailAddress";
import formatMessageSize from "../utils/formatMessageSize";
import classnames from "owa-classnames";
import loc from "owa-localize";
import { observer } from "owa-mobx-react";
import { isGlobalFeatureEnabled } from "owa-feature-flags";
import { getListViewColumnWidths, getSortedContentColumnHeaderType, type MailListColumnHeaderType, calculateAttachmentPreviewWellViewState, isAttachmentPreviewShortHeight } from "owa-mail-layout";
import { isReadingPanePositionRight } from "owa-mail-layout/lib/selectors/readingPanePosition";
import { onMailListItemClickHandler, useIsTypeRampUpdatesEnabled, useMailListItemContainerContext, showPersonaCheckbox, getPreviewTextStyles, } from "owa-mail-list-item-shared";
import type { TableView, MailFolderTableQuery } from "owa-mail-list-store";
import { type SortColumn } from "owa-mail-list-store/lib/store/schema/SortBy";
import { isNeedsReplyView } from "owa-mail-list-store";
import { isUnifiedInboxMailboxInfo } from "owa-client-types/lib/UnifiedInboxMailboxInfo";
import getListViewStore from "owa-mail-list-store/lib/store/Store";
import { NeedsReplyItemFeedback } from "owa-mail-needs-reply";
import { useComputedValue } from "owa-react-hooks/lib/useComputed";
import { full, medium, compact, secondColumn, itemView as mailListitemStyles_itemView, conversationView as mailListitemStyles_conversationView, categoryWellSingleLine, categoryWellSingleLineSortedColumn, categoryContainerSingleLine, previewDisplaySingleLine, reactionsContainer, mclRPRightThirdColumn, previewMultiLineMCLSpecific, } from "owa-mail-listitem-styles/lib/scss/MailListItem.scss";
import { firstRowBase, singleLineViewContainer, content, subjectUnread, subjectUnreadTypeRamp, subjectUnreadTypeRampFull, subjectUnreadTypeRampCompact, subject, subjectTypeRampFull, subjectTypeRampMedium, subjectTypeRampCompact, mclSecondRowStyles, shortAttachmentPreviewStyles, attachmentPreviewStyles, } from "owa-mail-listitem-styles/lib/scss/ListViewItemCommonStyles.scss";
import { firstColumnWithHeaders, firstColumnNoTwistyWithHeaders, mclLeadingControlsContainer, mclLeadingControlsContainerInlinePreview, mclLeadingControlsContainerNoTwisty, itemView, conversationView, standardizedFirstColumn, standardizedSecondColumn, flagDueDateStyle, redDark, sortedContentImportanceIcon, sortedContentPriorityIcon, sortedContentFlagStatusIcon, noCheckboxStyles, mclLeadingControlsContainerItemView, } from "./MailListItemSingleLineView.scss";
import { ImportantFilled, ArrowDownRegular, FlagFilled, CheckmarkFilled, } from "@fluentui/react-icons";
import { flagFilledColor } from "owa-mail-listitem-styles/lib/scss/FlagIconStyles.scss";
import { useMessageListTextStyle } from "owa-mail-listitem-styles/lib/utils/useMessageListTextStyle";
import MailListItemSelectionSource from "owa-mail-store/lib/store/schema/MailListItemSelectionSource";
import shouldShowUnstackedReadingPane from "owa-mail-organization-settings/lib/utils/shouldShowUnstackedReadingPane";
import type ReactListViewType from "owa-service/lib/contract/ReactListViewType";
import { getMailboxInfo } from "owa-mail-mailboxinfo";
import React from "react";
import type { CSSProperties } from "react";
import { MailListItemMarker, MailListItemFirstLineText, MailListItemIconBar, MailListItemLineText, MailListItemPreviewDisplay, MailListItemTwistyButton, MailListItemUnreadCount, PersonaCheckbox, MailListItemMeetingInfo, FolderTag, MailListItemRichPreviewWell, MailListItemTimestamp, MailListItemUnreadBar, } from "owa-mail-list-item-components";
import { SuggestedUnsubscribe } from "owa-suggested-unsubscribe-peek";
import { getDensityModeCssClass, getDensityModeString, getIsDarkTheme } from "owa-fabric-theme";
import { getConditionalFormattingStyles, ConditionalFormattingSurface, } from "owa-conditional-formatting";
import { senderEmailNotFoundLabel } from "./MailListItem.locstring.json";
import { CopilotInboxPriorityIcon } from "owa-mail-copilot-inbox-shared";
import { RECEIVED_COLUMN_LEFT_PADDING, MCL_RPRIGHT_LIST_ITEM_RIGHT_PADDING, MCL_LIST_ITEM_RIGHT_PADDING, DEFAULT_CF_PREVIEW_SENDER_COLUMN_WIDTH, DEFAULT_CF_PREVIEW_SUBJECT_COLUMN_WIDTH, DEFAULT_CF_PREVIEW_RECEIVED_COLUMN_WIDTH, } from "owa-mail-layout/lib/mailLayoutConstants";
import { PreviewLinesOption } from "owa-messagelist-layout-option";
import { getCategoryForCategorySortByRowKey } from "owa-mail-category-sort";
import shouldHideMessageListCheckboxes from "owa-messagelist-layout-option/lib/utils/shouldHideMessageListCheckboxes";
import isWin32StylingEnabled from "owa-theme-win32-style-store/lib/selectors/isWin32StylingEnabled";

const defaultExport: React.FunctionComponent<MailListItemSingleLineViewProps> = observer(function MailListItemSingleLineView(props: MailListItemSingleLineViewProps): React.JSX.Element {
    const { canArchive, canDelete, canPin, categories, conversationId, copilotInboxScore, copilotPrioritizeOnDeliveryOn, effectiveMentioned, firstLineText, firstLineTooltipText, hasAttachment, hasSharepointLink, highlightTerms, importance, isComplete, isFirstLevelExpanded, isFlagged, isInArchiveFolder, isInNotesFolder, isInOutboxFolder, isInJunkFolder, isLoadingSecondLevelExpansion, isPinned, renderMinimum, isSearchTable, isSecondLevelExpanded, isSelected, isSnoozed, isUnauthenticatedSender, itemClassIcon, lastDeliveryTimestamp, lastSender, latestItemId, listViewType, mailboxGuids, parentFolderDisplayName, parentFolderId, returnTime, rowId, rowKey, secondLineText, secondLineTooltipText, shouldShowExternalLabel, shouldShowTwisty, shouldSuggestUnsubscribe, showAttachmentPreview, showCondensedView, showDraftsIndicator, showFolderTag, showPreviewText, numMessagePreviewLines, smimeType, supportsFlagging, supportsHoverIcons, supportsMove, supportsPinning, tableViewId, thirdLineText, unreadCount, shouldShowRSVP, isReminderSet, isScheduledSend, isProactiveDraft, bulkCategory, isCfPreview, cfPreviewMods, cfPreviewBackgroundColorStyle, messageSizeBytes, flagDueDate, cfPreviewUnreadBarVars, } = props;
    
    const tableView: TableView = getListViewStore().tableViews.get(tableViewId) as TableView;
    
    const mailTableQuery = tableView?.tableQuery as MailFolderTableQuery;
    const isCategorySort = mailTableQuery?.sortBy?.sortColumn === 14;
    const isPrioritySort = mailTableQuery?.sortBy?.sortColumn === 16;
    
    // derived properties based on props/flights
    const densityModeClass = getDensityModeCssClass(full, medium, compact);
    const isUnread = unreadCount > 0;
    const shouldShowCategories = categories?.length > 0 && !isCategorySort;
    const showAttachmentPreviews = !showCondensedView && showAttachmentPreview;
    const notExpanded = !isFirstLevelExpanded && !isSecondLevelExpanded && !isLoadingSecondLevelExpansion;
    const showPreviews = !shouldShowUnstackedReadingPane() || notExpanded;
    const showPreviewTextConditionally = showPreviewText && notExpanded;
    const hasPreviewRow = showPreviewTextConditionally && numMessagePreviewLines > 0;
    // Both "Inline" and "0 Lines" render as a single compact row (no dedicated preview row).
    // "0 Lines" should match the Inline row height exactly, just without the preview text.
    const isSingleRowPreview = numMessagePreviewLines === PreviewLinesOption.Inline ||
        numMessagePreviewLines === PreviewLinesOption.Off;
    const showCheckbox = !isCfPreview &&
        (isGlobalFeatureEnabled("tri-reselect-checkbox-behavior") ||
            showPersonaCheckbox(isSearchTable, tableView));
    const mailboxInfo = props.mailboxInfo ?? getMailboxInfo(tableView);
    const onMissingCategory = useLogMissingCategory(tableViewId);
    const isItemView = tableView?.tableQuery.listViewType === 1;
    
    const unifiedInboxMailboxSmtpTooltip = isUnifiedInboxMailboxInfo(getMailboxInfo(tableView))
        ? mailboxInfo?.mailboxSmtpAddress
        : undefined;
    const { conditionalFormattingModifications: contextCfModifications } = useMailListItemContainerContext();
    const conditionalFormattingModifications = cfPreviewMods
        ? cfPreviewMods
        : contextCfModifications;
    
    const isDarkTheme = getIsDarkTheme();
    const isTypeRampUpdatesEnabled = useIsTypeRampUpdatesEnabled();
    const isWin32Style = isWin32StylingEnabled();
    const messageListTextStyle = useMessageListTextStyle("Major", undefined /* isFirstLine */, undefined /* isSingleLineView */, isWin32Style);
    const firstColumnStyles = isItemView || shouldShowTwisty
        ? firstColumnWithHeaders
        : firstColumnNoTwistyWithHeaders;
    
    const subjectClasses = classnames(isUnread
        ? isTypeRampUpdatesEnabled
            ? getDensityModeCssClass(subjectUnreadTypeRampFull, subjectUnreadTypeRamp, subjectUnreadTypeRampCompact)
            : subjectUnread
        : isTypeRampUpdatesEnabled
            ? getDensityModeCssClass(subjectTypeRampFull, subjectTypeRampMedium, subjectTypeRampCompact)
            : subject, (!isTypeRampUpdatesEnabled || isWin32Style) && messageListTextStyle);
    
    const sortedContentColumnHeaderType = React.useMemo(() => {
        return getSortedContentColumnHeaderType(tableView);
    }, [tableView]);
    
    const isReadingPaneRight = isReadingPanePositionRight();
    const { senderColumnWidth, subjectColumnWidth, receivedColumnWidth, sortedContentColumnWidth, } = getListViewColumnWidths(shouldHideMessageListCheckboxes(), sortedContentColumnHeaderType ?? undefined);
    
    const firstColumnWidth = React.useMemo(() => {
        return isCfPreview
            ? {
                width: `${DEFAULT_CF_PREVIEW_SENDER_COLUMN_WIDTH}%`,
                maxWidth: `${DEFAULT_CF_PREVIEW_SENDER_COLUMN_WIDTH}%`,
            }
            : {
                width: senderColumnWidth,
                maxWidth: senderColumnWidth,
            };
    }, [senderColumnWidth, isCfPreview]);
    
    const secondColumnWidth = React.useMemo(() => {
        return isCfPreview
            ? {
                width: `${DEFAULT_CF_PREVIEW_SUBJECT_COLUMN_WIDTH}%`,
                maxWidth: `${DEFAULT_CF_PREVIEW_SUBJECT_COLUMN_WIDTH}%`,
            }
            : {
                width: subjectColumnWidth,
                maxWidth: subjectColumnWidth,
            };
    }, [subjectColumnWidth, isCfPreview]);
    
    const thirdColumnWidth = React.useMemo<CSSProperties>(() => {
        return {
            width: isCfPreview
                ? `${DEFAULT_CF_PREVIEW_RECEIVED_COLUMN_WIDTH}%`
                : `${receivedColumnWidth}px`,
            maxWidth: isCfPreview
                ? `${DEFAULT_CF_PREVIEW_RECEIVED_COLUMN_WIDTH}%`
                : `${receivedColumnWidth}px`,
            boxSizing: "border-box",
            paddingLeft: `${RECEIVED_COLUMN_LEFT_PADDING}px`,
            paddingRight: isReadingPaneRight
                ? `${MCL_RPRIGHT_LIST_ITEM_RIGHT_PADDING}px`
                : `${MCL_LIST_ITEM_RIGHT_PADDING}px`,
        };
    }, [receivedColumnWidth, isReadingPaneRight, isCfPreview]);
    
    const ephemeralSortColumnWidth = React.useMemo(() => {
        return sortedContentColumnWidth > 0
            ? {
                width: sortedContentColumnWidth,
                maxWidth: sortedContentColumnWidth,
            }
            : undefined;
    }, [sortedContentColumnWidth]);
    
    let senderFirstLineText = firstLineText;
    
    // In the Junk Email folder, we'll show the sender's email address if the
    // sender's name is not a valid email address so users can determine the validity
    // of the sender's email address without opening potentially dangerous emails.
    if (isInJunkFolder && !isValidEmailAddress(firstLineText)) {
        const lastSenderMailboxEmailAddress = lastSender?.Mailbox?.EmailAddress;
        
        // If the sender's email address is valid, we'll show the sender's email address.
        // Otherwise, we'll show the sender's name and a label indicating that the sender's
        // email address is not found.
        if (lastSenderMailboxEmailAddress &&
            isValidEmailAddress(lastSenderMailboxEmailAddress)) {
            senderFirstLineText = `${firstLineText} <${lastSenderMailboxEmailAddress}>`;
        }
        else {
            senderFirstLineText = `${firstLineText} <${loc(senderEmailNotFoundLabel)}>`;
        }
    }
    
    const textStyle = React.useMemo(() => {
        return conditionalFormattingModifications &&
            conditionalFormattingModifications.length > 0
            ? getConditionalFormattingStyles(ConditionalFormattingSurface.Text, conditionalFormattingModifications, isDarkTheme, isSelected)
            : undefined;
    }, [conditionalFormattingModifications, isDarkTheme, isSelected]);
    
    const onPreviewClick = React.useCallback((evt: React.MouseEvent<unknown> | React.TouchEvent<unknown>) => {
        onMailListItemClickHandler(evt, MailListItemSelectionSource.MailListItemRichPreview, rowKey, tableViewId);
    }, [rowKey, tableViewId]);
    
    const attachmentPreviewWellViewState = getListViewStore().rowAttachmentPreviewWellViews.get(rowId);
    const previewViewState = attachmentPreviewWellViewState &&
        calculateAttachmentPreviewWellViewState(attachmentPreviewWellViewState, tableViewId, true);
    
    const folderTagComponent = (<FolderTag folderId={parentFolderId} parentFolderDisplayName={parentFolderDisplayName} isPreviewTextOn={showPreviewText} isSingleLine={true} mailboxGuids={mailboxGuids} mailboxInfo={mailboxInfo} shouldBeInTabOrder={isSelected}/>);
    
    const timestampComponent = returnTime ? (<MailListItemTimestamp isPinned={isPinned} isSingleLine={true} isSnoozed={true} returnTime={returnTime ?? ""} supportsPinning={supportsPinning} isCfPreview={isCfPreview} conditionalFormattingModifications={conditionalFormattingModifications} isDarkTheme={isDarkTheme} isSelected={isSelected}/>) : (<MailListItemTimestamp isPinned={isPinned} isSingleLine={true} isUnread={isUnread} lastDeliveryTimestamp={lastDeliveryTimestamp} returnTime={returnTime ?? ""} supportsPinning={supportsPinning} isCfPreview={isCfPreview} conditionalFormattingModifications={conditionalFormattingModifications} isDarkTheme={isDarkTheme} isSelected={isSelected}/>);
    
    const copilotInboxPriorityIcon = React.useMemo(() => (<CopilotInboxPriorityIcon copilotInboxScore={copilotInboxScore} scenario={isPrioritySort ? "PrioritySort" : "ML"} mailboxInfo={mailboxInfo} isUnread={isUnread} copilotPrioritizeOnDeliveryOn={copilotPrioritizeOnDeliveryOn}/>), [
        copilotInboxScore,
        isPrioritySort,
        mailboxInfo,
        isUnread,
        copilotPrioritizeOnDeliveryOn,
        sortedContentColumnHeaderType,
    ]);
    
    const sortedContentHeaderIconMemo = React.useMemo(() => {
        if (sortedContentColumnHeaderType === 4) {
            switch (importance) {
                case "Low":
                    return <ArrowDownRegular />;
                case "High":
                    return <ImportantFilled className={redDark}/>;
            }
        }
        else if (sortedContentColumnHeaderType === 5) {
            return copilotInboxScore > 0 ? copilotInboxPriorityIcon : "--";
        }
        else if (sortedContentColumnHeaderType === 6) {
            if (isComplete) {
                return <CheckmarkFilled />;
            }
            else if (isFlagged) {
                return <FlagFilled className={flagFilledColor}/>;
            }
            return null;
        }
        return null;
    }, [
        sortedContentColumnHeaderType,
        importance,
        copilotInboxScore,
        isPrioritySort,
        mailboxInfo,
        isUnread,
        copilotInboxPriorityIcon,
        copilotInboxScore,
        isComplete,
        isFlagged,
    ]);
    
    const renderIconBarComponent = (neverShowHoverIcons: boolean, neverShowPropertyIcons: boolean) => {
        return (<MailListItemIconBar canArchive={canArchive} canDelete={canDelete} canPin={canPin} effectiveMentioned={effectiveMentioned} hasAttachment={hasAttachment} hasSharepointLink={hasSharepointLink} importance={importance} isComplete={isComplete} isFlagged={isFlagged} isInArchiveFolder={isInArchiveFolder} isInNotesFolder={isInNotesFolder} isInOutboxFolder={isInOutboxFolder} isPinned={isPinned} isSingleLine={true} isSnoozed={isSnoozed} itemClassIcon={itemClassIcon} neverShowHoverIcons={neverShowHoverIcons || !supportsHoverIcons} supportsHoverIcons={supportsHoverIcons} neverShowPropertyIcons={neverShowPropertyIcons} rowKey={rowKey} smimeType={smimeType} supportsFlagging={supportsFlagging} supportsMove={supportsMove} supportsPinning={supportsPinning} tableViewId={tableViewId} unreadCount={unreadCount} isReminderSet={isReminderSet} hideImportancePropertyIcon={sortedContentColumnHeaderType === 4} hideFlagPropertyIcon={sortedContentColumnHeaderType === 6}/>);
    };
    
    const formattedFlagDueDate = useComputedValue(() => {
        if (sortedContentColumnHeaderType === 7) {
            return formatFlagDueDate(flagDueDate, ephemeralSortColumnWidth?.width ?? 0);
        }
        return null;
    }, [flagDueDate, sortedContentColumnHeaderType, ephemeralSortColumnWidth]);
    
    const rightSideIconBar = renderIconBarComponent(false /* neverShowHoverIcons */, true /* neverShowPropertyIcons */);
    
    const unreadBar = React.useMemo(() => {
        return (<MailListItemUnreadBar isExpanded={isFirstLevelExpanded || isSecondLevelExpanded} isUnread={isUnread} rowKey={rowKey} tableViewId={tableViewId} conditionalFormattingModifications={conditionalFormattingModifications} isDarkTheme={isDarkTheme} cfPreviewUnreadBarVars={cfPreviewUnreadBarVars}/>);
    }, [
        isFirstLevelExpanded,
        isSecondLevelExpanded,
        isUnread,
        rowKey,
        tableViewId,
        conditionalFormattingModifications,
        isDarkTheme,
        cfPreviewUnreadBarVars,
    ]);
    
    const sortedContentTextStyle = useMessageListTextStyle("Minor");
    
    return (<>
                {unreadBar}
                <div className={classnames(densityModeClass, getPreviewTextStyles(true, numMessagePreviewLines), shouldHideMessageListCheckboxes() && !shouldShowTwisty && noCheckboxStyles, mclLeadingControlsContainer, isSingleRowPreview && mclLeadingControlsContainerInlinePreview, !shouldShowTwisty && !isItemView && mclLeadingControlsContainerNoTwisty, shouldHideMessageListCheckboxes() &&
            isItemView &&
            mclLeadingControlsContainerItemView)} style={isCfPreview ? cfPreviewBackgroundColorStyle : undefined}>
                    {shouldShowTwisty && (<MailListItemTwistyButton densityMode={getDensityModeString()} isDarkTheme={isDarkTheme} isFirstLevelExpanded={isFirstLevelExpanded} renderMinimum={renderMinimum} isSecondLevelExpanded={isSecondLevelExpanded} isLoadingSecondLevelExpansion={isLoadingSecondLevelExpansion} isSelected={isSelected} isSingleLine={true} isUnread={unreadCount > 0} rowKey={rowKey} tableViewId={tableViewId}/>)}
                    {showCheckbox && (<PersonaCheckbox tableViewId={tableViewId} isSelected={isSelected} lastSender={lastSender} rowKey={rowKey} lastDeliveryTimestamp={lastDeliveryTimestamp} isUnauthenticatedSender={isUnauthenticatedSender} isCFPreview={isCfPreview}/>)}
                </div>
                <div className={classnames(singleLineViewContainer, densityModeClass, showPreviews && showAttachmentPreviews
            ? isAttachmentPreviewShortHeight(previewViewState?.numImageToShow ?? 0)
                ? shortAttachmentPreviewStyles
                : attachmentPreviewStyles
            : undefined, getPreviewTextStyles(true, numMessagePreviewLines))} style={isCfPreview ? cfPreviewBackgroundColorStyle : undefined} title={unifiedInboxMailboxSmtpTooltip}>
                    <div className={classnames(densityModeClass, firstRowBase)}>
                        <div className={classnames(firstColumnStyles, isItemView ? itemView : conversationView, densityModeClass, standardizedFirstColumn)} style={firstColumnWidth}>
                            <MailListItemMarker shouldShowExternalLabel={shouldShowExternalLabel} isUnauthenticatedSender={isUnauthenticatedSender} isSelected={isSelected} bulkCategory={bulkCategory}>
                                <MailListItemFirstLineText highlightTerms={highlightTerms} highlightAccuracy={tableView?.highlightAccuracy} showDraftsIndicator={showDraftsIndicator} isInOutboxFolder={isInOutboxFolder} unreadCount={unreadCount} text={senderFirstLineText} tooltipText={firstLineTooltipText ?? undefined} isSingleLineView={true} isScheduledSend={isScheduledSend} isProactiveDraft={isProactiveDraft} conditionalFormattingModifications={conditionalFormattingModifications} isDarkTheme={isDarkTheme} isUnread={isUnread} isSelected={isSelected}/>
                            </MailListItemMarker>
                            {!isReadingPaneRight
            ? renderIconBarComponent(false /* neverShowHoverIcons */, false /* neverShowPropertyIcons */)
            : renderIconBarComponent(true /* neverShowHoverIcons */, false /* neverShowPropertyIcons */)}
                        </div>
                        <div className={classnames(secondColumn, isItemView
            ? mailListitemStyles_itemView
            : mailListitemStyles_conversationView, densityModeClass, standardizedSecondColumn)} style={secondColumnWidth}>
                            {sortedContentColumnHeaderType !== 5 &&
            copilotInboxPriorityIcon}
                            {shouldShowCategories && (<LazyCategoryWell key={rowId} categoryWellContainerClass={categoryWellSingleLine} categoryContainerClass={categoryContainerSingleLine} categories={categories} onCategoryClicked={onCategoryClicked} onMissingCategory={onMissingCategory} mailboxInfo={mailboxInfo} conversationId={conversationId}/>)}
                            <div className={content}>
                                <div className={subjectClasses} style={textStyle}>
                                    <MailListItemLineText addOverflowStyles={true} highlightTerms={highlightTerms} text={secondLineText} tooltipText={secondLineTooltipText ?? undefined} conditionalFormattingModifications={conditionalFormattingModifications} isDarkTheme={isDarkTheme} isSelected={isSelected}/>
                                    <MailListItemUnreadCount unreadCount={unreadCount} conditionalFormattingModifications={conditionalFormattingModifications} isDarkTheme={isDarkTheme} isSingleLineView={true}/>
                                </div>
                                {showPreviewTextConditionally &&
            numMessagePreviewLines === PreviewLinesOption.Inline && (<div className={classnames(previewDisplaySingleLine)}>
                                            {showPreviews && (<MailListItemPreviewDisplay highlightTerms={highlightTerms} previewDisplay={thirdLineText} rowId={rowId} mailboxInfo={mailboxInfo} copilotPrioritizeOnDeliveryOn={copilotPrioritizeOnDeliveryOn} cfTextStyle={textStyle} isSelected={isSelected} numMessagePreviewLines={numMessagePreviewLines}/>)}
                                        </div>)}
                            </div>
                            {!hasPreviewRow &&
            !(isFirstLevelExpanded || isSecondLevelExpanded) &&
            renderReactions({
                latestItemId,
                reactionsContainerClassName: reactionsContainer,
                rowId,
                tableViewId,
            })}
                            {shouldShowRSVP && (<MailListItemMeetingInfo conversationId={conversationId} isSelected={isSelected} isSingleLineView={true} latestItemId={latestItemId} listViewType={listViewType} rowKey={rowKey}/>)}
                            {isNeedsReplyView(mailTableQuery) && (<NeedsReplyItemFeedback rowKey={rowKey} tableViewId={tableViewId} mailboxInfo={mailboxInfo}/>)}
                            {shouldSuggestUnsubscribe && (<SuggestedUnsubscribe isSingleLineView={true} isSelected={isSelected} rowId={rowId} tableView={tableView}/>)}
                            {showFolderTag && folderTagComponent}
                        </div>
                        {/* SLV SORTED CONTENT COLUMN STARTS HERE */}
                        {sortedContentColumnHeaderType && (<div className={classnames(secondColumn, isItemView
                ? mailListitemStyles_itemView
                : mailListitemStyles_conversationView, densityModeClass, standardizedSecondColumn)} style={ephemeralSortColumnWidth}>
                                <div className={content}>
                                    {sortedContentColumnHeaderType === 3 && (<LazyCategoryWell key={`${rowKey}-${getCategoryForCategorySortByRowKey(mailboxInfo, rowKey)}`} categoryWellContainerClass={categoryWellSingleLineSortedColumn} categoryContainerClass={categoryContainerSingleLine} categories={categories} onCategoryClicked={onCategoryClicked} onMissingCategory={onMissingCategory} mailboxInfo={mailboxInfo} conversationId={conversationId} sortedCategoryColumnHeaderValue={getCategoryForCategorySortByRowKey(mailboxInfo, rowKey)} sortedContentColumnWidth={sortedContentColumnWidth}/>)}
                                    {sortedContentColumnHeaderType === 8 && (<div className={sortedContentTextStyle}>
                                            <span>{formatMessageSize(messageSizeBytes)}</span>
                                        </div>)}
                                    {sortedContentColumnHeaderType === 4 && (<span className={sortedContentImportanceIcon}>
                                            {sortedContentHeaderIconMemo}
                                        </span>)}
                                    {sortedContentColumnHeaderType === 5 && (<span className={sortedContentPriorityIcon}>
                                            {sortedContentHeaderIconMemo}
                                        </span>)}
                                    {sortedContentColumnHeaderType === 6 && (<span className={sortedContentFlagStatusIcon}>
                                            {sortedContentHeaderIconMemo}
                                        </span>)}
                                    {sortedContentColumnHeaderType === 7 && (<div>
                                            <span className={classnames(sortedContentTextStyle, flagDueDate ? flagDueDateStyle : undefined)}>
                                                {formattedFlagDueDate}
                                            </span>
                                        </div>)}
                                </div>
                            </div>)}
                        {/* SLV THIRD COLUMN STARTS HERE */}
                        {<div style={thirdColumnWidth} className={mclRPRightThirdColumn}>
                                {timestampComponent}
                                {isReadingPaneRight && rightSideIconBar}
                            </div>}
                        {/* SLV THIRD COLUMN ENDS HERE */}
                    </div>
                    {hasPreviewRow && (<div className={classnames(mclSecondRowStyles)}>
                            <div className={classnames(previewDisplaySingleLine, previewMultiLineMCLSpecific)}>
                                {showPreviews && (<MailListItemPreviewDisplay highlightTerms={highlightTerms} previewDisplay={thirdLineText} rowId={rowId} mailboxInfo={mailboxInfo} cfTextStyle={textStyle} isSelected={isSelected} numMessagePreviewLines={numMessagePreviewLines} useTextFlowLayout={false}/>)}
                            </div>
                            {!(isFirstLevelExpanded || isSecondLevelExpanded) &&
                renderReactions({
                    latestItemId,
                    reactionsContainerClassName: reactionsContainer,
                    rowId,
                    tableViewId,
                })}
                        </div>)}
                    {showPreviews && showAttachmentPreviews && previewViewState && (<MailListItemRichPreviewWell isWideView={true} onPreviewClick={onPreviewClick} tableViewId={tableViewId} previewViewState={previewViewState} attachmentPreviewWellViewState={attachmentPreviewWellViewState}/>)}
                </div>
            </>);
}, "MailListItemSingleLineView");
export default defaultExport;
